import CountryRedirectModal from '@/components/CountryRedirect';
import ImageUpload from '@/components/ImageUpload';
import LuckySpinModal from '@/components/LuckySpin';
import useModalVersion from '@/hooks/modals/useModalVersion';
import { useResolvedTheme } from '@/hooks/theme/useResolvedTheme';
import { useSystemConfiguration } from '@/hooks/theme/useSystemConfiguration';
import { Setting } from '@starsoft/common/models';
import { OriginIdType } from '@starsoft/common/models/origin/id';
import { ComponentTypeId } from '@starsoft/common/models/setting/layout/component/type/types.enum';
import dynamic from 'next/dynamic';
import DepositBoost from '../../DepositBoost';

const SessionsModal = dynamic(() => import('@/components/Sessions/Modal'), {
  ssr: false,
});

const LanguageModal = dynamic(() => import('@/components/LanguageModal'), {
  ssr: false,
});
const GameSearchModal = dynamic(
  () => import('@/components/Home/V2/GameSearch/Modal'),
  { ssr: false },
);
const TrendTutorialModal = dynamic(
  () => import('@/components/Crash/TrendTutorialModal'),
  { ssr: false },
);
const ClaimRewardModal = dynamic(
  () => import('@/components/Bonus/ClaimRewardModal'),
  { ssr: false },
);

const WithdrawalModal = dynamic(
  () =>
    import('@starsoft/common/components').then(
      Mod => Mod.TransactionModalWithdraw,
    ),
  {
    ssr: false,
  },
);

const WithdrawVerifyModal = dynamic(
  () => import('@/components/Withdrawal/VerifyModal'),
  { ssr: false },
);
const CancelRegisterModal = dynamic(
  () => import('@/components/RegisterCancelModal'),
  { ssr: false },
);
const TermsModal = dynamic(() => import('@/components/Refer/TermsModal'), {
  ssr: false,
});

const VipLevelModal = dynamic(
  () => import('@/components/VipClub/VipLevelModal'),
  { ssr: false },
);

const LastRoundsModal = dynamic(
  () => import('@/components/core/Games/LastRounds/Modal'),
  {
    ssr: false,
  },
);

const LastBetsModal = dynamic(
  () => import('@/components/core/Games/LastBets/Modal'),
  {
    ssr: false,
  },
);

const GameReportModal = dynamic(
  () => import('@/components/core/Games/GameReportModal'),
  {
    ssr: false,
  },
);

const ProfileModal = dynamic(() => import('@/components/ProfileModal'), {
  ssr: false,
});

const UnavailableBalanceModal = dynamic(
  () => import('@/components/core/Games/UnavailableBalanceModal/'),
  {
    ssr: false,
  },
);

const WalletModal = dynamic(() => import('@/components/WalletModal'), {
  ssr: false,
});

const CoinConversionModal = dynamic(
  () => import('@/components/CoinConversionModal'),
  { ssr: false },
);
const DepositModal = dynamic(
  () =>
    import('@starsoft/common/components').then(
      Mod => Mod.TransactionModalDeposit,
    ),
  { ssr: false },
);

const NotificationModal = dynamic(
  () => import('@/components/Notifications/NotificationModal'),
  { ssr: false },
);

const AuthModalsOptions = dynamic(
  () => import('@/components/AuthModal/Content'),
  { ssr: false },
);

const BonusOverviewModal = dynamic(
  () => import('@/components/Bonus/BonusOverviewModal'),
  { ssr: false },
);

const ClaimBonusModal = dynamic(
  () => import('@/components/Bonus/UserBonus/Modal'),
  { ssr: false },
);

const ProfileDrawer = dynamic(() => import('@/components/Profile/Drawer'), {
  ssr: false,
});

const RollPointGame = dynamic(() => import('@/components/RollCompetition'), {
  ssr: false,
});
const RestrictionModal = dynamic(
  () => import('@/components/Restrictions/Modal'),
  {
    ssr: false,
  },
);

const SelfExclusionModal = dynamic(
  () => import('@/components/Restrictions/SelfExclusion/Modal'),
  {
    ssr: false,
  },
);
const RestrictionDetailsModal = dynamic(
  () => import('@/components/Restrictions/DetailsModal'),
  {
    ssr: false,
  },
);

export default function MainLayoutModals({ setting }: { setting: Setting }) {
  const { components } = useResolvedTheme(setting);
  const { version } = useModalVersion();

  const { originId } = useSystemConfiguration();

  const authComponentId = components.get(
    ComponentTypeId.Login?.toString(),
  ) as string;

  return (
    <>
      <AuthModalsOptions id={authComponentId} />
      <CancelRegisterModal />
      <BonusOverviewModal />
      <RollPointGame />
      <LanguageModal />
      <GameSearchModal />
      <WithdrawalModal
        originId={originId as unknown as OriginIdType}
        version={version}
      />
      <DepositModal
        additionalContent={<DepositBoost />}
        originId={originId as unknown as OriginIdType}
        version={version}
      />
      <WithdrawVerifyModal />
      <TermsModal />
      <GameReportModal />
      <TrendTutorialModal />
      <LuckySpinModal />
      <RollPointGame />
      <RestrictionDetailsModal />
      <VipLevelModal />
      <ClaimRewardModal />
      <CountryRedirectModal />
      <LastRoundsModal />
      <LastBetsModal />
      <ProfileModal />
      <UnavailableBalanceModal />
      <RestrictionModal />
      <ProfileDrawer />
      <WalletModal />
      <CoinConversionModal />
      <NotificationModal />
      <ClaimBonusModal />
      <SelfExclusionModal />
      <SessionsModal />
      <ImageUpload />
    </>
  );
}
